// ═══════════════════════════════════════════════════════════════════
//  TOQUM · ADMIN Module
//  Users & rights (approve/edit/entry/view) · Masters · Brand Quality
//  Process & timelines · Allocations · Defect & ISO codes
// ═══════════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { T, Page, CardRow, Split, uid, today, Section, Field, Grid, Btn, DataTable, TabBar, Tag, Badge,
         StatCard, Modal, Toast, apiPost, isManager, isSuperAdmin } from './Shared';
import { FORMAT_REGISTRY } from './FormatSchemas';
import { FormatRegistryTable } from './InspectionForms';

const RIGHTS  = ['view','entry','edit','approve'];
const MODULES = ['ADMIN','TEAM','SUPPLIER','STYLE','QA','DASHBOARD'];

// ─── 1. USER MANAGEMENT ────────────────────────────────────────────
const BLANK_USER = { username:'', name:'', email:'', phone:'', designation:'', location:'',
  role:'User', password:'', confirm:'', status:'Active', skipVerification:true,
  mustChangePassword:true, rights:{ DASHBOARD:['view'] } };

function UserManagement({ db, setDb, saveToDB, currentUser }) {
  const [editUser, setEditUser] = useState(null);
  const [addOpen, setAddOpen]   = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [toast, setToast]       = useState(null);
  const notify = (msg, type='info') => { setToast({ msg, type }); setTimeout(()=>setToast(null), 4000); };
  const pending = db.pendingUsers || [];
  const users   = db.users || [];

  const approvePending = async (p, role) => {
    if (!isManager(currentUser)) return notify('Only a Manager/Admin can approve new users.', 'error');
    const rights = p.rights && Object.keys(p.rights).length ? p.rights : { DASHBOARD:['view'] };
    const entry = { ...p, role, status:'Active', approvedBy: currentUser.name, approvedOn: today(), rights };
    try {
      const res = await apiPost('auth.php', { action:'approve_user', username:p.username,
        role, rights, approvedBy: currentUser.name });
      if (!res.success) return notify(res.error || 'Approval failed', 'error');
    } catch { notify('Backend unreachable — approved locally only.', 'warn'); }
    setDb(x => ({ ...x,
      users:[...users, entry],
      pendingUsers: pending.filter(q => q.id !== p.id) }));
    setEditUser(entry); // proceed straight to rights approval
  };

  const rejectPending = async (p) => {
    if (!window.confirm(`Reject registration of ${p.username}?`)) return;
    try { await apiPost('auth.php', { action:'reject_user', username:p.username }); } catch {}
    setDb(x => ({ ...x, pendingUsers: pending.filter(q => q.id !== p.id) }));
  };

  // ── Admin creates a user directly (no self-registration needed) ──
  const createUser = async (f) => {
    if (!isManager(currentUser)) return notify('Manager rights required to create users.', 'error');
    for (const k of ['username','name','email','password'])
      if (!f[k]) return notify(`${k[0].toUpperCase()+k.slice(1)} is required`, 'error');
    if (f.password !== f.confirm)   return notify('Passwords do not match', 'error');
    if (f.password.length < 8)      return notify('Password must be at least 8 characters', 'error');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return notify('Enter a valid email', 'error');
    if ([...users, ...pending].some(u => u.username === f.username))
      return notify('Username already exists', 'error');

    const entry = { id: uid(), username:f.username, name:f.name, email:f.email, phone:f.phone,
      designation:f.designation, location:f.location, role:f.role, status:f.status,
      emailVerified: !!f.skipVerification, mustChangePassword: !!f.mustChangePassword,
      rights:f.rights, createdBy: currentUser.name, approvedBy: currentUser.name, approvedOn: today() };

    try {
      const res = await apiPost('auth.php', { action:'create_user', ...f, createdBy: currentUser.name });
      if (!res.success) return notify(res.error || 'Could not create user', 'error');
    } catch (e) {
      notify('Backend unreachable — user created locally only.', 'warn');
    }
    setDb(p => ({ ...p, users:[...users, entry] }));
    setAddOpen(false);
    notify(`User "${f.username}" created.${f.skipVerification ? '' : ' A verification code was emailed.'}`, 'success');
    setEditUser(entry);
  };

  const resetPassword = async (u, newPw) => {
    if (!isManager(currentUser)) return notify('Manager rights required.', 'error');
    if ((newPw||'').length < 8) return notify('Password must be at least 8 characters', 'error');
    try {
      const res = await apiPost('auth.php', { action:'reset_password', username:u.username,
        newPassword:newPw, resetBy: currentUser.name });
      if (!res.success) return notify(res.error || 'Reset failed', 'error');
    } catch { notify('Backend unreachable — reset applied locally only.', 'warn'); }
    setDb(p => ({ ...p, users: users.map(x => x.id===u.id ? { ...x, mustChangePassword:true } : x) }));
    setResetFor(null);
    notify(`Password reset for ${u.username}. They must change it at next login.`, 'success');
  };

  const toggleRight = (mod, right) => {
    setEditUser(u => {
      const cur = u.rights?.[mod] || [];
      const next = cur.includes(right) ? cur.filter(r => r!==right) : [...cur, right];
      return { ...u, rights:{ ...u.rights, [mod]: next } };
    });
  };

  const saveRights = async () => {
    if (!isManager(currentUser)) return notify('Only a Manager/Admin can approve rights.', 'error');
    try {
      const res = await apiPost('auth.php', { action:'set_rights', username:editUser.username,
        rights: editUser.rights || {}, role: editUser.role });
      if (!res.success) return notify(res.error || 'Could not save rights', 'error');
    } catch { notify('Backend unreachable — rights saved locally only.', 'warn'); }
    setDb(x => ({ ...x, users: users.map(u => u.id===editUser.id ? editUser : u) }));
    notify(`Rights updated for ${editUser.username}`, 'success');
    setEditUser(null);
  };

  const deactivate = async (u) => {
    if (u.role==='Super Admin') return;
    const status = u.status==='Active' ? 'Deactivated' : 'Active';
    try { await apiPost('auth.php', { action:'set_status', username:u.username, status }); } catch {}
    setDb(p => ({ ...p, users: users.map(x => x.id===u.id ? { ...x, status } : x) }));
  };

  return (
    <div>
      {pending.length > 0 && (
        <Section title={`⏳ Pending Registrations — Manager Approval Required (${pending.length})`}>
          <DataTable columns={['User ID','Name','Email','Verified','Requested']}
            rows={pending}
            renderCell={(r,c)=>({ 'User ID':r.username,'Name':r.name,'Email':r.email,
              'Verified': r.emailVerified ? <Badge status="Verified"/> : <Badge status="Pending"/>,
              'Requested': r.requestedOn }[c])}
            actions={r => (<span style={{ display:'flex', gap:5 }}>
              <select id={`role-${r.id}`} defaultValue="User" style={{ fontSize:11, padding:3 }}>
                <option>User</option><option>QA</option><option>Manager</option><option>Admin</option>
              </select>
              <Btn small color={T.success} label="Approve" onClick={()=>approvePending(r, document.getElementById(`role-${r.id}`).value)} />
              <Btn small color={T.danger} label="Reject" onClick={()=>rejectPending(r)} />
            </span>)} />
        </Section>
      )}

      <CardRow min={190}>
        <StatCard label="Total Users"   value={users.length} color={T.primary} />
        <StatCard label="Active"        value={users.filter(u=>u.status==='Active').length} color={T.success} />
        <StatCard label="Pending Approval" value={pending.length} color={pending.length?T.warning:T.textSub} />
        <StatCard label="Deactivated"   value={users.filter(u=>u.status==='Deactivated').length} color={T.danger} />
        <StatCard label="Managers/Admins" value={users.filter(u=>['Manager','Admin','Super Admin'].includes(u.role)).length} color={T.purple} />
      </CardRow>

      <Section title="Registered Users"
        actions={isManager(currentUser) &&
          <Btn small label="+ Add User" color={T.success} onClick={()=>setAddOpen(true)} />}>
        <DataTable columns={['User ID','Name','Email','Role','Status','Rights Summary']}
          rows={users}
          renderCell={(r,c)=>({ 'User ID':<b>{r.username}</b>,'Name':r.name,'Email':r.email,'Role':r.role,
            'Status':<Badge status={r.status}/>,
            'Rights Summary': r.role==='Super Admin' ? <Tag label="FULL ACCESS" color={T.purple}/> :
              MODULES.filter(m => (r.rights?.[m]||[]).length).map(m => <Tag key={m} label={`${m}: ${(r.rights[m]).join('/')}`} />) }[c])}
          actions={r => (<span style={{ display:'flex', gap:5 }}>
            <Btn small outline label="Rights" onClick={()=>setEditUser(r)} />
            {isManager(currentUser) && <Btn small outline color={T.warning} label="Reset PW" onClick={()=>setResetFor(r)} />}
            {r.role!=='Super Admin' && <Btn small outline color={r.status==='Active'?T.danger:T.success}
              label={r.status==='Active'?'Deactivate':'Activate'} onClick={()=>deactivate(r)} />}
          </span>)} />
      </Section>

      {editUser && (
        <Section title={`Rights Approval — ${editUser.username} (${editUser.role})`}
          actions={<Btn small color="#64748b" label="Close" onClick={()=>setEditUser(null)} />}>
          <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%', maxWidth:640 }}>
            <thead><tr>
              <th style={{ textAlign:'left', padding:8 }}>Module</th>
              {RIGHTS.map(r => <th key={r} style={{ padding:8, textTransform:'capitalize' }}>{r}</th>)}
            </tr></thead>
            <tbody>{MODULES.map(m => (
              <tr key={m} style={{ borderTop:'1px solid #f1f5f9' }}>
                <td style={{ padding:8, fontWeight:700 }}>{m}</td>
                {RIGHTS.map(r => (
                  <td key={r} style={{ padding:8, textAlign:'center' }}>
                    <input type="checkbox" checked={(editUser.rights?.[m]||[]).includes(r)} onChange={()=>toggleRight(m,r)} />
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
          <div style={{ marginTop:12 }}><Btn label="Approve & Save Rights" color={T.success} onClick={saveRights} /></div>
        </Section>
      )}

      {addOpen && <AddUserModal onClose={()=>setAddOpen(false)} onCreate={createUser} />}
      {resetFor && <ResetPasswordModal user={resetFor} onClose={()=>setResetFor(null)} onReset={resetPassword} />}
      <Toast msg={toast?.msg} type={toast?.type} onClose={()=>setToast(null)} />
    </div>
  );
}

// ── Add User modal ───────────────────────────────────────────────
function AddUserModal({ onClose, onCreate }) {
  const [f, setF] = useState({ ...BLANK_USER });
  const set = (k,v) => setF(p=>({ ...p, [k]:v }));
  const toggleRight = (mod, right) => setF(p => {
    const cur = p.rights?.[mod] || [];
    return { ...p, rights:{ ...p.rights,
      [mod]: cur.includes(right) ? cur.filter(r=>r!==right) : [...cur, right] } };
  });
  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$';
    const pw = Array.from({length:12},()=>chars[Math.floor(Math.random()*chars.length)]).join('');
    setF(p=>({ ...p, password:pw, confirm:pw }));
  };
  return (
    <Modal title="Add New User" width={860} onClose={onClose}
      actions={<>
        <Btn label="Cancel" color="#64748b" onClick={onClose} />
        <Btn label="Create User" color={T.success} onClick={()=>onCreate(f)} />
      </>}>
      <Grid cols={3}>
        <Field label="Full Name *" value={f.name} onChange={e=>set('name',e.target.value)} />
        <Field label="User ID *" value={f.username} onChange={e=>set('username',e.target.value)} placeholder="login name" />
        <Field label="Email *" value={f.email} onChange={e=>set('email',e.target.value)} />
        <Field label="Phone" value={f.phone} onChange={e=>set('phone',e.target.value)} />
        <Field label="Designation" value={f.designation} onChange={e=>set('designation',e.target.value)} placeholder="e.g. Senior QA" />
        <Field label="Location" value={f.location} onChange={e=>set('location',e.target.value)} />
        <Field label="Role *" value={f.role} onChange={e=>set('role',e.target.value)}
          options={['User','QA','Manager','Admin','Super Admin']} />
        <Field label="Status" value={f.status} onChange={e=>set('status',e.target.value)}
          options={['Active','Pending Approval','Deactivated']} />
        <div />
        <Field label="Password *" type="password" value={f.password} onChange={e=>set('password',e.target.value)} />
        <Field label="Confirm Password *" type="password" value={f.confirm} onChange={e=>set('confirm',e.target.value)} />
        <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}>
          <Btn small outline label="Generate" onClick={genPassword} />
        </div>
      </Grid>
      <div style={{ display:'flex', gap:20, flexWrap:'wrap', marginBottom:12, fontSize:12 }}>
        <label><input type="checkbox" checked={f.skipVerification}
          onChange={e=>set('skipVerification',e.target.checked)} /> Skip email verification (mark verified now)</label>
        <label><input type="checkbox" checked={f.mustChangePassword}
          onChange={e=>set('mustChangePassword',e.target.checked)} /> Force password change at first login</label>
      </div>
      <div style={{ fontSize:11.5, color:T.textSub, background:'#f8fafc', padding:'8px 10px',
        borderRadius:6, marginBottom:12 }}>
        Unchecking "skip email verification" emails the user a 6-digit code they must enter before
        they can log in. Rights can be set below now, or later from the Rights button.
      </div>
      <b style={{ fontSize:11.5 }}>Module Rights</b>
      <table style={{ borderCollapse:'collapse', fontSize:12, width:'100%', maxWidth:620, marginTop:6 }}>
        <thead><tr><th style={{ textAlign:'left', padding:6 }}>Module</th>
          {RIGHTS.map(r=><th key={r} style={{ padding:6, textTransform:'capitalize' }}>{r}</th>)}</tr></thead>
        <tbody>{MODULES.map(m=>(
          <tr key={m} style={{ borderTop:'1px solid #f1f5f9' }}>
            <td style={{ padding:6, fontWeight:700 }}>{m}</td>
            {RIGHTS.map(r=>(
              <td key={r} style={{ padding:6, textAlign:'center' }}>
                <input type="checkbox" checked={(f.rights?.[m]||[]).includes(r)} onChange={()=>toggleRight(m,r)} />
              </td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    </Modal>
  );
}

// ── Reset password modal ─────────────────────────────────────────
function ResetPasswordModal({ user, onClose, onReset }) {
  const [pw, setPw] = useState('');
  return (
    <Modal title={`Reset Password — ${user.username}`} width={420} onClose={onClose}
      actions={<>
        <Btn label="Cancel" color="#64748b" onClick={onClose} />
        <Btn label="Reset Password" color={T.warning} onClick={()=>onReset(user, pw)} />
      </>}>
      <Field label="New Password (min 8 characters)" type="password" value={pw} onChange={e=>setPw(e.target.value)} />
      <div style={{ fontSize:11.5, color:T.textSub }}>
        {user.name} will be required to set their own password at next login.
        Share this temporary password with them through a secure channel, not email.
      </div>
    </Modal>
  );
}

// ─── 2. MASTERS (brands, countries, regions, cities, categories) ───
function SimpleMaster({ title, listKey, db, setDb, saveToDB, extraFields=[] }) {
  const [val, setVal] = useState(''); const [extras, setExtras] = useState({});
  const list = db[listKey] || [];
  const add = async () => {
    if (!val.trim()) return;
    const entry = typeof list[0] === 'object' || extraFields.length
      ? { id: uid(), name: val.trim(), ...extras }
      : val.trim();
    if (list.some(x => (x.name||x) === val.trim())) return alert('Already exists');
    setDb(p => ({ ...p, [listKey]: [...list, entry] }));
    await saveToDB(listKey, entry);
    setVal(''); setExtras({});
  };
  const del = (item) => {
    if (!window.confirm('Delete this entry?')) return;
    setDb(p => ({ ...p, [listKey]: list.filter(x => x !== item) }));
  };
  const rename = (item) => {
    const nn = window.prompt('Edit name:', item.name || item);
    if (!nn) return;
    setDb(p => ({ ...p, [listKey]: list.map(x => x===item ? (typeof x==='object'?{...x,name:nn}:nn) : x) }));
  };
  return (
    <Section title={title}>
      <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
        <input value={val} onChange={e=>setVal(e.target.value)} placeholder={`Add ${title}...`}
          onKeyDown={e=>e.key==='Enter'&&add()}
          style={{ padding:'7px 10px', border:'1px solid #cbd5e1', borderRadius:5, fontSize:12, flex:'1 1 160px' }} />
        {extraFields.map(f => (
          <select key={f.key} value={extras[f.key]||''} onChange={e=>setExtras({...extras,[f.key]:e.target.value})}
            style={{ padding:'7px', border:'1px solid #cbd5e1', borderRadius:5, fontSize:12 }}>
            <option value="">{f.label}</option>
            {f.options.map(o => <option key={o.name||o}>{o.name||o}</option>)}
          </select>
        ))}
        <Btn small label="+ Add" onClick={add} />
      </div>
      <div>{list.map((item,i) => (
        <Tag key={i} color={T.primary}
          label={<span onDoubleClick={()=>rename(item)} title="Double-click to edit">
            {(item.name||item)}{item.country?` · ${item.country}`:''}{item.region?` · ${item.region}`:''}</span>}
          onRemove={()=>del(item)} />
      ))}</div>
    </Section>
  );
}

// ─── 3. BRAND QUALITY PROCESS (step-wise + timeline from PO date) ──
function QualityProcess({ db, setDb, saveToDB }) {
  const [brand, setBrand] = useState('');
  const [step, setStep] = useState({ name:'', daysFromPO:'', mandatory:true, format:'', description:'' });
  const brands = (db.brands||[]).map(b => b.name||b);
  const procs = (db.qualityProcess||[]).filter(p => p.brand===brand).sort((a,b)=>a.seq-b.seq);

  const addStep = async () => {
    if (!brand) return alert('Select a brand first');
    if (!step.name || step.daysFromPO==='') return alert('Step name and timeline (days from PO) are required');
    const entry = { id: uid(), brand, seq: procs.length+1, ...step, daysFromPO: parseInt(step.daysFromPO,10) };
    setDb(p => ({ ...p, qualityProcess:[...(p.qualityProcess||[]), entry] }));
    await saveToDB('qualityProcess', entry);
    setStep({ name:'', daysFromPO:'', mandatory:true, format:'', description:'' });
  };
  const delStep = (s) => setDb(p => ({ ...p,
    qualityProcess:(p.qualityProcess||[]).filter(x => x.id!==s.id)
      .map(x => x.brand===brand && x.seq>s.seq ? { ...x, seq:x.seq-1 } : x) }));
  const move = (s, dir) => {
    const other = procs.find(x => x.seq === s.seq + dir);
    if (!other) return;
    setDb(p => ({ ...p, qualityProcess:(p.qualityProcess||[]).map(x =>
      x.id===s.id ? {...x,seq:x.seq+dir} : x.id===other.id ? {...x,seq:x.seq-dir} : x) }));
  };

  // Standard steps derived from the Tarasima J-Section format list
  const loadTemplate = () => {
    if (!brand) return alert('Select a brand first');
    const tpl = [
      ['Pre-Production Meeting (PPM)',        5,  'PPM'],
      ['Manufacturing Control Plan (MCP)',    7,  'MCP'],
      ['Risk Assessment & Preventive Action', 8,  'RISK'],
      ['Fabric Inspection & Shrinkage Tests', 12, ''],
      ['Pilot Run / Size-Set Approval',       15, ''],
      ['Cutting Inline Inspection',           20, 'INSP'],
      ['Sewing Inline Inspection',            28, 'INSP'],
      ['Finishing Inline Inspection',         38, 'INSP'],
      ['Pre-Final Inspection',                45, 'INSP'],
      ['Final Inspection (AQL)',              50, 'INSP'],
      ['FMEA Review & Closure',               52, 'FMEA'],
    ];
    const entries = tpl.map(([name,d,fmt],i) => ({ id:uid()+i, brand, seq:procs.length+i+1, name,
      daysFromPO:d, mandatory:true, format:fmt, description:'' }));
    setDb(p => ({ ...p, qualityProcess:[...(p.qualityProcess||[]), ...entries] }));
  };

  return (
    <Section title="Brand Quality Process — Step-wise, timeline offset from PO generation date"
      actions={<Btn small outline label="Load J-Format Template" onClick={loadTemplate} />}>
      <Grid cols={5}>
        <Field label="Brand" value={brand} onChange={e=>setBrand(e.target.value)} options={brands} />
        <Field label="Process Step Name" value={step.name} onChange={e=>setStep({...step,name:e.target.value})} placeholder="e.g. Pre-Final Inspection" />
        <Field label="Days from PO Date" type="number" value={step.daysFromPO} onChange={e=>setStep({...step,daysFromPO:e.target.value})} />
        <Field label="Inspection Format" value={step.format} onChange={e=>setStep({...step,format:e.target.value})}
          options={FORMAT_REGISTRY.map(f=>`${f.code} — ${f.subject}`)} />
        <div style={{ display:'flex', alignItems:'flex-end', gap:8, paddingBottom:10, flexWrap:'wrap' }}>
          <label style={{ fontSize:11.5 }}><input type="checkbox" checked={step.mandatory} onChange={e=>setStep({...step,mandatory:e.target.checked})} /> Mandatory (gates next step)</label>
          <Btn small label="+ Add Step" onClick={addStep} />
        </div>
      </Grid>
      <div style={{ fontSize:11, color:T.textSub, background:'#f8fafc', padding:'8px 10px', borderRadius:6, marginBottom:10 }}>
        The format decides which J-section form the QA fills for this step. Steps named to match a format
        (PPM, MCP, Risk, Inspection, FMEA) map automatically; anything else is recorded manually.
      </div>
      {brand && <DataTable columns={['Seq','Process Step','Format','Timeline (PO + days)','Mandatory','Order']}
        rows={procs} emptyMsg="No process steps for this brand yet."
        renderCell={(r,c)=>({ 'Seq':<b>{r.seq}</b>,'Process Step':r.name,
          'Format': (()=>{ const f=FORMAT_REGISTRY.find(x=>x.code===(r.format||'').split(' ')[0]);
            return f ? <Tag label={f.iso.split('-').slice(-2).join('-')} color={T.teal} /> : <Tag label="manual" color={T.textSub} />; })(),
          'Timeline (PO + days)':`PO date + ${r.daysFromPO} days`,
          'Mandatory': r.mandatory ? <Badge status="Approved"/> : <Badge status="Draft"/>,
          'Order': <span><Btn small outline label="↑" onClick={()=>move(r,-1)} /> <Btn small outline label="↓" onClick={()=>move(r,1)} /></span> }[c])}
        actions={r => <Btn small color={T.danger} label="✕" onClick={()=>delStep(r)} />} />}
    </Section>
  );
}

// ─── 4. ALLOCATIONS (supplier → brand · supplier → QA) ─────────────
function Allocations({ db, setDb, saveToDB, currentUser }) {
  const [ba, setBa] = useState({ supplier:'', brand:'' });
  const [qa, setQa] = useState({ supplier:'', qaMember:'' });
  const suppliers = (db.suppliers||[]).map(s => s.name);
  const brands = (db.brands||[]).map(b => b.name||b);
  const qaUsers = (db.users||[]).filter(u => ['QA','Manager','User'].includes(u.role)).map(u => u.name);
  const canAllocate = isManager(currentUser);

  const addBrandAlloc = async () => {
    if (!ba.supplier || !ba.brand) return;
    const entry = { id: uid(), ...ba, allocatedBy: currentUser.name, date: today() };
    setDb(p => ({ ...p, brandAllocations:[...(p.brandAllocations||[]), entry] }));
    await saveToDB('brandAllocation', entry); setBa({ supplier:'', brand:'' });
  };
  const addQaAlloc = async () => {
    if (!canAllocate) return alert('Supplier allocation to QA is done by Manager with admin rights only.');
    if (!qa.supplier || !qa.qaMember) return;
    const entry = { id: uid(), ...qa, allocatedBy: currentUser.name, date: today() };
    setDb(p => ({ ...p, qaAllocations:[...(p.qaAllocations||[]), entry] }));
    await saveToDB('qaAllocation', entry); setQa({ supplier:'', qaMember:'' });
  };

  return (
    <div>
      <Section title="Allocate Supplier → Brand">
        <Grid cols={3}>
          <Field label="Supplier" value={ba.supplier} onChange={e=>setBa({...ba,supplier:e.target.value})} options={suppliers} />
          <Field label="Brand" value={ba.brand} onChange={e=>setBa({...ba,brand:e.target.value})} options={brands} />
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}><Btn label="Allocate" onClick={addBrandAlloc} /></div>
        </Grid>
        <DataTable columns={['Supplier','Brand','By','Date']} rows={db.brandAllocations||[]}
          renderCell={(r,c)=>({ 'Supplier':r.supplier,'Brand':r.brand,'By':r.allocatedBy,'Date':r.date }[c])}
          actions={r => <Btn small color={T.danger} label="✕" onClick={()=>setDb(p=>({...p,brandAllocations:p.brandAllocations.filter(x=>x.id!==r.id)}))} />} />
      </Section>
      <Section title={`Allocate Supplier → QA Member ${canAllocate ? '' : '(Manager rights required)'}`}>
        <Grid cols={3}>
          <Field label="Supplier" value={qa.supplier} onChange={e=>setQa({...qa,supplier:e.target.value})} options={suppliers} disabled={!canAllocate} />
          <Field label="QA Team Member" value={qa.qaMember} onChange={e=>setQa({...qa,qaMember:e.target.value})} options={qaUsers} disabled={!canAllocate} />
          <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}><Btn label="Allocate" onClick={addQaAlloc} disabled={!canAllocate} /></div>
        </Grid>
        <DataTable columns={['Supplier','QA Member','By','Date']} rows={db.qaAllocations||[]}
          renderCell={(r,c)=>({ 'Supplier':r.supplier,'QA Member':r.qaMember,'By':r.allocatedBy,'Date':r.date }[c])}
          actions={r => canAllocate && <Btn small color={T.danger} label="✕" onClick={()=>setDb(p=>({...p,qaAllocations:p.qaAllocations.filter(x=>x.id!==r.id)}))} />} />
      </Section>
    </div>
  );
}

// ─── 5. DEFECT CODES & ISO CODES (entry + CSV upload) ──────────────
function CodeMaster({ title, listKey, db, setDb, saveToDB, cols }) {
  const [f, setF] = useState(Object.fromEntries(cols.map(c => [c.key,''])));
  const list = db[listKey] || [];
  const add = async () => {
    if (!f[cols[0].key]) return alert(`${cols[0].label} required`);
    const entry = { id: uid(), ...f };
    setDb(p => ({ ...p, [listKey]:[...list, entry] }));
    await saveToDB(listKey.replace(/s$/,''), entry);
    setF(Object.fromEntries(cols.map(c => [c.key,''])));
  };
  const upload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const lines = ev.target.result.split(/\r?\n/).filter(l => l.trim());
      const start = lines[0].toLowerCase().includes(cols[0].label.toLowerCase()) ? 1 : 0; // skip header
      const entries = lines.slice(start).map(l => {
        const parts = l.split(',').map(x => x.trim());
        return { id: uid()+Math.random(), ...Object.fromEntries(cols.map((c,i) => [c.key, parts[i]||''])) };
      });
      setDb(p => ({ ...p, [listKey]:[...list, ...entries] }));
      alert(`${entries.length} codes uploaded.`);
    };
    reader.readAsText(file); e.target.value='';
  };
  return (
    <Section title={title}
      actions={<label style={{ fontSize:11, fontWeight:700, color:T.primary, cursor:'pointer' }}>
        📤 Upload CSV<input type="file" accept=".csv,.txt" onChange={upload} style={{ display:'none' }} /></label>}>
      <Grid cols={cols.length+1}>
        {cols.map(c => <Field key={c.key} label={c.label} value={f[c.key]} onChange={e=>setF({...f,[c.key]:e.target.value})} options={c.options} />)}
        <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:10 }}><Btn small label="+ Add" onClick={add} /></div>
      </Grid>
      <DataTable columns={cols.map(c=>c.label)} rows={list}
        renderCell={(r,c)=>r[cols.find(x=>x.label===c).key]}
        actions={r => <Btn small color={T.danger} label="✕" onClick={()=>setDb(p=>({...p,[listKey]:list.filter(x=>x.id!==r.id)}))} />} />
    </Section>
  );
}

// ─── QUALITY VISION / MISSION / GOALS ───────────────────────────────
function QualityVisionAdmin({ db, setDb, saveToDB }) {
  const saved   = db.qualityVision || {};
  const [f, setF] = useState({
    vision:   saved.vision   || '',
    mission:  saved.mission  || '',
    values:   saved.values   || '',
    goals:    saved.goals    || [{ text:'', target:'', unit:'', deadline:'' }],
    year:     saved.year     || new Date().getFullYear(),
    showOnLogin:  saved.showOnLogin  ?? true,
    showOnLogout: saved.showOnLogout ?? true,
  });
  const [saved_, setSaved_] = useState(false);

  const save = async () => {
    const rec = { ...f, updatedAt: new Date().toISOString() };
    setDb(p=>({ ...p, qualityVision: rec }));
    await saveToDB('qualityVision', { id:'qualityVision', ...rec });
    setSaved_(true); setTimeout(()=>setSaved_(false), 2500);
  };

  const setGoal = (i, k, v) => setF(p=>({ ...p,
    goals: p.goals.map((g,gi)=>gi===i?{...g,[k]:v}:g) }));

  return (
    <div>
      <Section title={`Quality Vision, Mission & Goals — ${f.year}`}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
          <div>
            <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
              textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
              Quality Vision *
            </label>
            <textarea value={f.vision} rows={4}
              onChange={e=>setF(p=>({...p,vision:e.target.value}))}
              placeholder="e.g. To be the most trusted quality partner in the garment industry — delivering zero-defect products that delight our customers every time."
              style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${T.border}`,
                borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box',
                fontFamily:"'Inter',sans-serif" }}/>
          </div>
          <div>
            <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
              textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
              Quality Mission *
            </label>
            <textarea value={f.mission} rows={4}
              onChange={e=>setF(p=>({...p,mission:e.target.value}))}
              placeholder="e.g. We commit to building robust quality systems at every factory, empowering our QA team with data, and partnering with suppliers to continuously improve."
              style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${T.border}`,
                borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box',
                fontFamily:"'Inter',sans-serif" }}/>
          </div>
        </div>
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:10.5, fontWeight:700, color:T.textSub,
            textTransform:'uppercase', letterSpacing:'0.06em', display:'block', marginBottom:4 }}>
            Core Quality Values
          </label>
          <textarea value={f.values} rows={2}
            onChange={e=>setF(p=>({...p,values:e.target.value}))}
            placeholder="e.g. Integrity · Accountability · Continuous Improvement · Customer First · Zero Compromise"
            style={{ width:'100%', padding:'9px 12px', border:`1.5px solid ${T.border}`,
              borderRadius:8, fontSize:13, resize:'vertical', boxSizing:'border-box',
              fontFamily:"'Inter',sans-serif" }}/>
        </div>
      </Section>

      <Section title={`Annual Quality Goals — ${f.year}`} actions={
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <label style={{ fontSize:11, color:T.textSub, fontWeight:600 }}>Year:</label>
          <input type="number" value={f.year} onChange={e=>setF(p=>({...p,year:parseInt(e.target.value)}))}
            style={{ width:72, padding:'4px 8px', border:`1px solid ${T.border}`, borderRadius:5, fontSize:12 }}/>
        </div>
      }>
        <div style={{ overflowX:'auto' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12 }}>
            <thead><tr style={{ background:'linear-gradient(90deg,#0a0e1a,#1a2236)', color:'white' }}>
              {['#','Goal / KPI','Target Value','Unit','Deadline',''].map(h=>(
                <th key={h} style={{ padding:'8px 10px', textAlign:'left', fontWeight:700 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{f.goals.map((g,i)=>(
              <tr key={i} style={{ borderBottom:`1px solid ${T.border}`,
                background:i%2===0?'white':'#fafbfd' }}>
                <td style={{ padding:'6px 10px', color:T.textSub, fontWeight:700 }}>{i+1}</td>
                <td style={{ padding:'6px 6px' }}>
                  <input value={g.text} onChange={e=>setGoal(i,'text',e.target.value)}
                    placeholder="e.g. Reduce DHU below 3.0 across all suppliers"
                    style={{ width:'100%', padding:'6px 8px', border:`1px solid ${T.border}`,
                      borderRadius:5, fontSize:12 }}/>
                </td>
                <td style={{ padding:'6px 6px' }}>
                  <input value={g.target} onChange={e=>setGoal(i,'target',e.target.value)}
                    placeholder="e.g. 3.0"
                    style={{ width:80, padding:'6px 8px', border:`1px solid ${T.border}`,
                      borderRadius:5, fontSize:12, textAlign:'center' }}/>
                </td>
                <td style={{ padding:'6px 6px' }}>
                  <input value={g.unit} onChange={e=>setGoal(i,'unit',e.target.value)}
                    placeholder="DHU / % / pcs"
                    style={{ width:90, padding:'6px 8px', border:`1px solid ${T.border}`,
                      borderRadius:5, fontSize:12 }}/>
                </td>
                <td style={{ padding:'6px 6px' }}>
                  <input type="date" value={g.deadline} onChange={e=>setGoal(i,'deadline',e.target.value)}
                    style={{ padding:'6px 8px', border:`1px solid ${T.border}`,
                      borderRadius:5, fontSize:11 }}/>
                </td>
                <td style={{ padding:'6px 6px' }}>
                  <button onClick={()=>setF(p=>({...p,goals:p.goals.filter((_,gi)=>gi!==i)}))}
                    style={{ background:'none', border:'none', color:T.danger,
                      cursor:'pointer', fontSize:16 }}>×</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <button onClick={()=>setF(p=>({...p,goals:[...p.goals,{text:'',target:'',unit:'',deadline:''}]}))}
          style={{ marginTop:8, padding:'6px 14px', background:'white',
            border:`1.5px solid ${T.primary}`, color:T.primary, borderRadius:6,
            fontSize:11.5, fontWeight:700, cursor:'pointer' }}>
          + Add Goal
        </button>
      </Section>

      <Section title="Reminder Settings">
        <div style={{ display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer',
            fontSize:13, fontWeight:500, color:T.textMain }}>
            <input type="checkbox" checked={f.showOnLogin}
              onChange={e=>setF(p=>({...p,showOnLogin:e.target.checked}))}
              style={{ width:16, height:16, accentColor:T.primary }}/>
            Show Vision & Goals reminder on first login of the day
          </label>
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer',
            fontSize:13, fontWeight:500, color:T.textMain }}>
            <input type="checkbox" checked={f.showOnLogout}
              onChange={e=>setF(p=>({...p,showOnLogout:e.target.checked}))}
              style={{ width:16, height:16, accentColor:T.primary }}/>
            Show daily closing summary & goals reminder before logout
          </label>
        </div>
        <div style={{ marginTop:14, display:'flex', gap:10 }}>
          <Btn label={saved_?'✓ Saved!':'Save Vision & Goals'}
            color={saved_?T.success:T.primary} onClick={save}/>
        </div>
      </Section>

      {/* Preview */}
      {(f.vision||f.mission||f.goals.some(g=>g.text)) && (
        <Section title="Preview — How it appears to staff">
          <QualityVisionCard vision={f} compact={false}/>
        </Section>
      )}
    </div>
  );
}

// ─── QUALITY VISION CARD (reused in login reminder) ─────────────────
export function QualityVisionCard({ vision, onClose, compact=true }) {
  if (!vision?.vision && !vision?.goals?.length) return null;
  const v = vision;
  return (
    <div style={{ background:'linear-gradient(135deg,#0a0e1a 0%,#1a2236 100%)',
      borderRadius:compact?16:12, overflow:'hidden',
      boxShadow: compact?'0 24px 64px rgba(0,0,0,0.5)':'none' }}>
      {/* Header */}
      <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.08)',
        display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.12em',
            textTransform:'uppercase', color:'rgba(255,255,255,0.4)', marginBottom:4 }}>
            Toqum Quality System · {v.year}
          </div>
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
            fontSize:compact?22:18, fontWeight:900, color:'white', lineHeight:1.2 }}>
            Our Quality Commitment
          </div>
        </div>
        {onClose && (
          <button onClick={onClose}
            style={{ background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)',
              color:'white', borderRadius:6, padding:'5px 12px', cursor:'pointer',
              fontSize:11, fontWeight:700 }}>
            Start Work →
          </button>
        )}
      </div>

      <div style={{ padding:'18px 24px' }}>
        {/* Vision & Mission */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:18 }}>
          {[['🎯 Vision', v.vision], ['🚀 Mission', v.mission]].filter(([,txt])=>txt).map(([label,txt])=>(
            <div key={label} style={{ background:'rgba(255,255,255,0.06)',
              borderRadius:10, padding:'14px 16px',
              borderLeft:'3px solid #4f6ef7' }}>
              <div style={{ fontSize:10, fontWeight:800, color:'#4f6ef7',
                textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>{label}</div>
              <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.85)', lineHeight:1.65 }}>{txt}</div>
            </div>
          ))}
        </div>

        {/* Values */}
        {v.values && (
          <div style={{ textAlign:'center', marginBottom:18 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.35)',
              textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:8 }}>Core Values</div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, justifyContent:'center' }}>
              {v.values.split(/[·,|]+/).map(val=>val.trim()).filter(Boolean).map(val=>(
                <span key={val} style={{ padding:'5px 14px', background:'rgba(79,110,247,0.2)',
                  border:'1px solid rgba(79,110,247,0.4)', borderRadius:20,
                  fontSize:11.5, fontWeight:700, color:'#a0b4ff' }}>{val}</span>
              ))}
            </div>
          </div>
        )}

        {/* Goals */}
        {v.goals?.filter(g=>g.text).length > 0 && (
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.35)',
              textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:10 }}>
              {v.year} Quality Goals
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {v.goals.filter(g=>g.text).map((g,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10,
                  background:'rgba(255,255,255,0.05)', borderRadius:8, padding:'9px 12px' }}>
                  <span style={{ width:22, height:22, borderRadius:'50%',
                    background:'linear-gradient(135deg,#4f6ef7,#06d6a0)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:10, fontWeight:900, color:'white', flexShrink:0 }}>{i+1}</span>
                  <span style={{ flex:1, fontSize:12.5, color:'rgba(255,255,255,0.85)' }}>{g.text}</span>
                  {g.target && (
                    <span style={{ fontSize:12, fontWeight:800, color:'#06d6a0',
                      background:'rgba(6,214,160,0.15)', padding:'3px 10px',
                      borderRadius:6, flexShrink:0 }}>
                      Target: {g.target} {g.unit}
                    </span>
                  )}
                  {g.deadline && (
                    <span style={{ fontSize:10.5, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>
                      by {g.deadline}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN ROOT ─────────────────────────────────────────────────────
export default function AdminModule({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['USERS','MASTERS','QUALITY PROCESS','ALLOCATION','DEFECT CODES','FORMAT REGISTER','QUALITY VISION'];
  const [tab, setTab] = useState('USERS');
  const countries = (db.countries||[]);
  const regions = (db.regions||[]);
  return (
    <Page title="ADMINISTRATION" subtitle="Users & rights · masters · brand quality process · allocations · defect codes · format register">
      <TabBar tabs={TABS} active={tab} onChange={setTab} />
      {tab==='USERS' && <UserManagement db={db} setDb={setDb} saveToDB={saveToDB} currentUser={currentUser} />}
      {tab==='MASTERS' && <div>
        <SimpleMaster title="Brands" listKey="brands" db={db} setDb={setDb} saveToDB={saveToDB} />
        <SimpleMaster title="Country of Production" listKey="countries" db={db} setDb={setDb} saveToDB={saveToDB} />
        <SimpleMaster title="Regions" listKey="regions" db={db} setDb={setDb} saveToDB={saveToDB}
          extraFields={[{ key:'country', label:'Country', options:countries }]} />
        <SimpleMaster title="Cities" listKey="cities" db={db} setDb={setDb} saveToDB={saveToDB}
          extraFields={[{ key:'region', label:'Region', options:regions }]} />
        <SimpleMaster title="Supplier Categories" listKey="supplierCategories" db={db} setDb={setDb} saveToDB={saveToDB} />
      </div>}
      {tab==='QUALITY PROCESS' && <QualityProcess db={db} setDb={setDb} saveToDB={saveToDB} />}
      {tab==='ALLOCATION' && <Allocations db={db} setDb={setDb} saveToDB={saveToDB} currentUser={currentUser} />}
      {tab==='DEFECT CODES' && <CodeMaster title="Defect Code Master (CSV: Code, Description, Category, Severity)"
        listKey="defectCodes" db={db} setDb={setDb} saveToDB={saveToDB}
        cols={[{key:'code',label:'Code'},{key:'description',label:'Description'},
               {key:'category',label:'Category',options:['Cutting','Sewing','Washing','Finishing','Packing','Fabric','Trims','Measurement','Safety']},
               {key:'severity',label:'Severity',options:['Critical','Major','Minor']}]} />}
      {tab==='FORMAT REGISTER' && <FormatRegistryTable records={db.qualityRecords||[]} />}
      {tab==='QUALITY VISION'  && <QualityVisionAdmin db={db} setDb={setDb} saveToDB={saveToDB} />}
    </Page>
  );
}
